import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Spin, Pagination, Input, Button, Switch, Modal, Tag, Checkbox, Tour } from 'antd';
import { FilterOutlined, SearchOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useSearchBehavior } from '../hooks/useSearchBehavior';
import { format } from 'date-fns';
import { formatCurrency, formatPhoneNumber } from '../util';
import { useUpdateCustomerStatus, GET_CUSTOMERS, CREATE_CUSTOMER, UPDATE_CUSTOMER, useUpdateCustomerBotStatus, useUnmarkAsAiBot } from '../mutations/customers';
import CustomerForm from '../components/customers/CustomerForm';
import UpdateStatusBotModal from '../components/customers/UpdateStatusBotModal';
import AiBotDetectedBadge from '../components/customers/AiBotDetectedBadge';
import UnmarkAiBotModal from '../components/customers/UnmarkAiBotModal';
import CustomerImportModal from '../components/CustomerImportModal';
import CustomerDateFilterModal from '../components/customers/CustomerDateFilterModal';
import AdvancedFilterModal from '../components/customers/AdvancedFilterModal';
import ActiveFilters from '../components/customers/ActiveFilters';
import ActiveAdvancedFilters from '../components/customers/ActiveAdvancedFilters';
import FiltersSummary from '../components/customers/FiltersSummary';
import MaintenanceModal from '../components/customers/MaintenanceModal';


const Customers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Usa o hook customizado para gerenciar o comportamento de busca
  const {
    searchTerm,
    setSearchTerm,
    activeSearchTerm,
    handleManualSearch,
    clearSearch,
    isInstantSearch
  } = useSearchBehavior("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isBotModalVisible, setIsBotModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isDateFilterModalVisible, setIsDateFilterModalVisible] = useState(false);
  const [isAdvancedFilterModalVisible, setIsAdvancedFilterModalVisible] = useState(false);
  const [dateFilters, setDateFilters] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [isMaintenanceModalVisible, setIsMaintenanceModalVisible] = useState(false);
  const [isUnmarkModalVisible, setIsUnmarkModalVisible] = useState(false);
  const [selectedCustomerForUnmark, setSelectedCustomerForUnmark] = useState(null);


  const searchInputRef = useRef(null);
  const newCustomerButtonRef = useRef(null);
  const [onboardingTourOpen, setOnboardingTourOpen] = useState(false);
  const [updateCustomerStatus] = useUpdateCustomerStatus();
  const [updateCustomerBotStatus] = useUpdateCustomerBotStatus();
  const [unmarkAsAiBot, { loading: unmarkLoading }] = useUnmarkAsAiBot();
  const [createCustomer] = useMutation(CREATE_CUSTOMER, {
    // Remove o update do cache para evitar problemas
    // O refetch será feito manualmente após a criação
  });
  const [updateCustomer] = useMutation(UPDATE_CUSTOMER);

  const handleBotStatusUpdate = async (newBotStatus, disableDuration) => {
    try {
      await updateCustomerBotStatus({
        variables: { id: selectedCustomer.id, botStatus: newBotStatus, disableDuration },
        update: (cache, { data: { updateCustomerBotStatus } }) => {
          const { customer } = updateCustomerBotStatus;
          cache.modify({
            id: cache.identify(customer),
            fields: {
              botStatus() {
                return newBotStatus;
              },
              botDisabledUntil() {
                return customer.botDisabledUntil;
              },
              timeUntilReactivation() {
                return customer.timeUntilReactivation;
              },
            },
          });
        }
      });
      setIsBotModalVisible(false);
    } catch (error) {
      console.error(error);
    }
  };

  const showBotStatusModal = (customer) => {
    setSelectedCustomer(customer);
    setIsBotModalVisible(true);
  };

  const handleCancelBotModal = () => {
    setIsBotModalVisible(false);
  };

  const handleOpenUnmarkModal = (customer) => {
    setSelectedCustomerForUnmark(customer);
    setIsUnmarkModalVisible(true);
  };

  const handleUnmarkAsBot = async () => {
    try {
      await unmarkAsAiBot({
        variables: { id: selectedCustomerForUnmark.id },
        update: (cache, { data: { unmarkAsAiBot } }) => {
          const { customer } = unmarkAsAiBot;
          
          cache.modify({
            id: cache.identify(customer),
            fields: {
              aiBotDetected() { return false; },
              aiBotDetectedAt() { return null; },
              aiBotDetectionReason() { return null; },
              repeatedMessageCount() { return 0; },
              lastMessageHash() { return null; }
            }
          });
        }
      });
      
      setIsUnmarkModalVisible(false);
      setSelectedCustomerForUnmark(null);
      Modal.success({
        content: 'Cliente desbloqueado com sucesso!',
      });
    } catch (error) {
      console.error('Erro ao desbloquear cliente:', error);
      Modal.error({
        title: 'Erro ao desbloquear cliente',
        content: 'Ocorreu um erro ao tentar desbloquear o cliente. Tente novamente.',
      });
    }
  };

  // Remove o useEffect do debounce, agora é gerenciado pelo hook customizado

  const { loading, error, data, refetch } = useQuery(GET_CUSTOMERS, {
    variables: {
      page: currentPage,
      perPage: pageSize,
      search: activeSearchTerm,
      activeOnly: !advancedFilters.some(filter => filter.field === 'active'),
      dateFilters: dateFilters.map(filter => ({
        field: filter.field,
        filterType: filter.filterType || filter.type,
        startDate: filter.startDate,
        endDate: filter.endDate,
        month: filter.month,
        day: filter.day,
        year: filter.year
      })),
      advancedFilters: advancedFilters.map(filter => ({
        field: filter.field,
        operator: filter.operator,
        value: filter.value,
        fieldType: filter.fieldType
      }))
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (location.state?.onboardingStep === 'create_first_customer') {
      setOnboardingTourOpen(true);
    }
  }, [location.state?.onboardingStep]);

  const showConfirmModal = (customer) => {
    setSelectedCustomer(customer);
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    setIsModalVisible(false);
    const { id, active } = selectedCustomer;
    try {
      await updateCustomerStatus({
        variables: { id, active: !active },
        update: (cache, { data: { updateCustomerStatus } }) => {
          const { customer } = updateCustomerStatus;
          cache.modify({
            id: cache.identify(customer),
            fields: {
              active(cachedActive) {
                return !active;
              }
            }
          });
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSearch = () => {
    if (!isInstantSearch) {
      handleManualSearch();
    }
    setCurrentPage(1);
  };

  const handleTableChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
    // Limpar seleção ao mudar de página
    setSelectedCustomers([]);
  };

  const handleCreateCustomer = () => {
    setOnboardingTourOpen(false);
    setIsEditMode(false);
    setSelectedCustomer({ active: true });
    setIsFormModalVisible(true);
  };

  const handleFormCancel = () => {
    setIsFormModalVisible(false);
    setSelectedCustomer(null);
    setErrorMessage(null);
  };

  const cleanData = (values) => {
    return {
      ...values,
      emailsAttributes: values?.emailsAttributes?.map(email => ({
        email: email.email,
        description: email.description,
        active: email.active,
      })),
      addressesAttributes: values?.addressesAttributes?.map(address => ({
        id: address.id,
        zipcode: address.zipcode,
        cityId: address.cityId.value || address.cityId,
        description: address.description,
        district: address.district,
        street: address.street,
        number: address.number,
        complement: address.complement,
        main: address.main,
      })),
    };
  };

  const handleSave = async (values) => {
    setErrorMessage(null);
    // Conversão de botStatus já é feita no CustomerForm.jsx
    // Não fazer conversão duplicada aqui para evitar sobrescrever

    try {
      const isCreating = !isEditMode;

      if (isEditMode) {
        const updatedValues = cleanData(values)
        const response = await updateCustomer({
          variables: { id: selectedCustomer.id, attributes: updatedValues }
        });

        const errors = response.data?.updateCustomer?.errors;
        if (errors && errors.length > 0) {
          setErrorMessage(errors.join(', '));
          return;
        }
      } else {
        const response = await createCustomer({
          variables: { attributes: values }
        });

        const errors = response.data?.createCustomer?.errors;
        if (errors && errors.length > 0) {
          setErrorMessage(errors.join(', '));
          return;
        }
      }

      // Se chegou até aqui, a operação foi bem-sucedida
      console.log('Cliente salvo com sucesso, fazendo refetch...');
      // Sempre refetch para garantir que os dados estejam atualizados
      await refetch();
      handleFormCancel();
      if (isCreating) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      setErrorMessage('Ocorreu um erro inesperado.');
    }
  };

  const handleEditCustomer = (customer) => {
    setIsEditMode(true);
    setSelectedCustomer(customer)
    setIsFormModalVisible(true);
  };

  const showImportModal = () => {
    setIsImportModalVisible(true);
  };

  const handleImportCancel = () => {
    setIsImportModalVisible(false);
  };

  const showDateFilterModal = () => {
    setIsDateFilterModalVisible(true);
  };

  const handleDateFilterCancel = () => {
    setIsDateFilterModalVisible(false);
  };

  const handleDateFilterApply = (filters) => {
    setDateFilters(filters);
    setCurrentPage(1); // Reset para primeira página quando aplicar filtros
    setSelectedCustomers([]); // Limpar seleção ao aplicar filtros
    setIsDateFilterModalVisible(false);
  };

  const showAdvancedFilterModal = () => {
    setIsAdvancedFilterModalVisible(true);
  };

  const handleAdvancedFilterCancel = () => {
    setIsAdvancedFilterModalVisible(false);
  };

  const handleAdvancedFilterApply = (filters) => {
    setAdvancedFilters(filters);
    setCurrentPage(1); // Reset para primeira página quando aplicar filtros
    setSelectedCustomers([]); // Limpar seleção ao aplicar filtros
    setIsAdvancedFilterModalVisible(false);
  };

  const getActiveFiltersCount = () => {
    return dateFilters.length;
  };

  const getActiveAdvancedFiltersCount = () => {
    return advancedFilters.length;
  };

  const getTotalActiveFiltersCount = () => {
    return dateFilters.length + advancedFilters.length;
  };

  const handleRemoveFilter = (filterIndex) => {
    const newFilters = dateFilters.filter((_, index) => index !== filterIndex);
    setDateFilters(newFilters);
    setCurrentPage(1);
  };

  const handleRemoveAdvancedFilter = (filterIndex) => {
    const newFilters = advancedFilters.filter((_, index) => index !== filterIndex);
    setAdvancedFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setDateFilters([]);
    setCurrentPage(1);
  };

  const handleClearAllAdvancedFilters = () => {
    setAdvancedFilters([]);
    setCurrentPage(1);
  };

  // Handlers para checkbox de seleção
  const handleSelectCustomer = (customerId, checked) => {
    if (checked) {
      setSelectedCustomers(prev => [...prev, customerId]);
    } else {
      setSelectedCustomers(prev => prev.filter(id => id !== customerId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allCustomerIds = customers.map(customer => customer.id);
      setSelectedCustomers(allCustomerIds);
    } else {
      setSelectedCustomers([]);
    }
  };

  // Handlers para modal de manutenção em massa
  const showMaintenanceModal = () => {
    setIsMaintenanceModalVisible(true);
  };

  const handleMaintenanceModalClose = () => {
    setIsMaintenanceModalVisible(false);
  };

  if (error) return <p>Error: {error.message}</p>;

  const customers = data?.customers?.edges.map(edge => edge?.node) || [];
  
  const isAllSelected = customers.length > 0 && selectedCustomers.length === customers.length;
  const isIndeterminate = selectedCustomers.length > 0 && selectedCustomers.length < customers.length;

  const columns = [
    {
      title: (
        <Checkbox
          indeterminate={isIndeterminate}
          checked={isAllSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'checkbox',
      key: 'checkbox',
      width: 50,
      fixed: 'left',
      render: (_, record) => (
        <Checkbox
          checked={selectedCustomers.includes(record.id)}
          onChange={(e) => handleSelectCustomer(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Código',
      dataIndex: 'reference',
      key: 'reference',
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
    },
    {
      title: 'Apelido',
      dataIndex: 'nickname',
      key: 'nickname',
    },
    {
      title: 'Contato Principal',
      dataIndex: 'mainContact',
      key: 'mainContact',
      render: (text) => text ? (
        <div style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
          {text}
        </div>
      ) : '-',
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', maxWidth: '140px' }}>
          {tags && tags.length > 0 ? (
            tags.slice(0, 3).map(tag => (
              <Tag
                key={tag.id}
                style={{
                  backgroundColor: tag.backgroundColor || '#6fa8dc',
                  color: tag.fontColor || '#000000',
                  border: 'none',
                  borderRadius: '3px',
                  fontSize: '10px',
                  padding: '1px 4px',
                  margin: '1px',
                  lineHeight: '14px',
                  maxWidth: '60px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={tag.name}
              >
                {tag.name}
              </Tag>
            )).concat(
              tags.length > 3 ? [
                <span
                  key="more"
                  style={{
                    fontSize: '10px',
                    color: '#999',
                    fontWeight: 'bold',
                    padding: '1px 2px'
                  }}
                  title={`+${tags.length - 3} tag(s): ${tags.slice(3).map(t => t.name).join(', ')}`}
                >
                  +{tags.length - 3}
                </span>
              ] : []
            )
          ) : (
            <span style={{ color: '#ccc', fontSize: '11px' }}>-</span>
          )}
        </div>
      ),
    },
    {
      title: 'WhatsApp',
      dataIndex: 'whatsapp',
      key: 'whatsapp',
      fixed: 'left',
      render: (whatsapp, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>{formatPhoneNumber(whatsapp)}</span>
          {whatsapp && (
            record.whatsappVerified ? (
              <CheckOutlined style={{ color: '#52c41a', fontSize: '12px' }} title="WhatsApp Validado" />
            ) : (
              <CloseOutlined style={{ color: '#ff4d4f', fontSize: '12px' }} title="WhatsApp Não Validado" />
            )
          )}
        </div>
      ),
    },
    {
      title: 'Endereços',
      dataIndex: 'addresses',
      key: 'addresses',
      render: (addresses) => (
        <div>
          {addresses.map((address, index) => (
            <div key={index}>
              {address.fullAddress}
              {index < addresses.length - 1 && <br />}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: 'Documento',
      dataIndex: 'document',
      key: 'document',
    },
    {
      title: 'Última Venda',
      dataIndex: 'lastSale',
      key: 'lastSale',
      render: (text) => text ? format(new Date(text), 'dd/MM/yyyy HH:mm') : '-',
    },
    {
      title: 'Data de Nascimento',
      dataIndex: 'birthDate',
      key: 'birthDate',
      render: (text) => text ? format(new Date(text), 'dd/MM/yyyy') : '-',
    },
    {
      title: 'Status Bot',
      key: 'botStatus',
      width: 180,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {record.botStatus === 'bot_disabled' && (
            <Tag 
              color="orange"
              style={{ cursor: 'pointer', fontSize: '11px' }}
              onClick={() => showBotStatusModal(record)}
            >
              Bot Desativado {record.timeUntilReactivation && `(${record.timeUntilReactivation})`}
            </Tag>
          )}
          <AiBotDetectedBadge 
            customer={record} 
            onUnmark={handleOpenUnmarkModal}
          />
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      fixed: 'right',
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={(checked) => showConfirmModal(record)}
        />
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      fixed: 'right',
      render: (text, record) => (
        <Button type="link" onClick={() => handleEditCustomer(record)}>Editar</Button>
      ),
    },
  ];
  const { totalCount } = data?.customers || { pageInfo: {}, totalCount: 0 };
  
  // Configuração das opções de tamanho de página
  const pageSizeOptions = ['10', '20', '30', '50', '100'];
  if (totalCount > 100) {
    pageSizeOptions.push(totalCount.toString());
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
        <Input
          ref={searchInputRef}
          placeholder={isInstantSearch ? "Buscar clientes (busca automática)" : "Buscar clientes"}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onPressEnter={handleSearch}
          style={{ marginRight: 8, width: 300 }}
          autoFocus
          allowClear={true}
        />
        {!isInstantSearch && (
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            Pesquisar
          </Button>
        )}
        <Button
          type={getActiveFiltersCount() > 0 ? "primary" : "default"}
          icon={<FilterOutlined />}
          onClick={showDateFilterModal}
          style={{ marginLeft: 8 }}
        >
          Filtros Data
          {getActiveFiltersCount() > 0 && (
            <span style={{ marginLeft: 4, backgroundColor: '#ff4d4f', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '10px' }}>
              {getActiveFiltersCount()}
            </span>
          )}
        </Button>
        <Button
          type={getActiveAdvancedFiltersCount() > 0 ? "primary" : "default"}
          icon={<FilterOutlined />}
          onClick={showAdvancedFilterModal}
          style={{ marginLeft: 8 }}
        >
          Filtros Avançados
          {getActiveAdvancedFiltersCount() > 0 && (
            <span style={{ marginLeft: 4, backgroundColor: '#ff4d4f', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '10px' }}>
              {getActiveAdvancedFiltersCount()}
            </span>
          )}
        </Button>

        {selectedCustomers.length > 0 && (
          <Button 
            type="default" 
            style={{ marginLeft: 'auto', marginRight: '8px' }}
            onClick={showMaintenanceModal}
          >
            Manutenção em Massa ({selectedCustomers.length})
          </Button>
        )}
        <Button
          ref={newCustomerButtonRef}
          type="primary" 
          onClick={handleCreateCustomer} 
          style={{ marginLeft: selectedCustomers.length > 0 ? '0' : 'auto' }}
        >
          Novo Cliente
        </Button>
        <Tour
          open={onboardingTourOpen}
          onClose={() => setOnboardingTourOpen(false)}
          steps={[
            {
              title: 'Cadastre seu primeiro cliente',
              description: 'Clique em Novo Cliente para abrir o formulário. No formulário, preencha os campos obrigatórios marcados com *: Nome e WhatsApp. Depois clique em Criar para concluir esta etapa.',
              target: () => newCustomerButtonRef.current,
              placement: 'left'
            }
          ]}
        />
        <Button type="default" style={{ marginLeft: '5px' }} onClick={showImportModal}>
          Importar Clientes
        </Button>
      </div>
      <ActiveFilters
        filters={dateFilters}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={handleClearAllFilters}
      />
      <ActiveAdvancedFilters
        filters={advancedFilters}
        onRemoveFilter={handleRemoveAdvancedFilter}
        onClearAll={handleClearAllAdvancedFilters}
      />
      {/* Paginação superior - alinhada à direita */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={totalCount}
          locale={{ items_per_page: '/ página' }}
          onChange={handleTableChange}
          pageSizeOptions={pageSizeOptions}
          showSizeChanger={true}
        />
      </div>

      <Spin spinning={loading}>
        <Table
          dataSource={customers}
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Spin>
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={totalCount}
          locale={{ items_per_page: '/ página' }}
          onChange={handleTableChange}
          pageSizeOptions={pageSizeOptions}
          showSizeChanger={true}
        />
        <FiltersSummary
          totalCount={totalCount}
          filtersCount={getTotalActiveFiltersCount()}
        />
      </div>
      <Modal
        title="Criar Cliente"
        open={isFormModalVisible}
        onCancel={handleFormCancel}
        footer={null}
        maskClosable={false}
        width={1200}
        getContainer={false}
      >
        <CustomerForm
          initialValues={selectedCustomer}
          onFinish={handleSave}
          onCancel={handleFormCancel}
          isEditMode={isEditMode}
          errorMessage={errorMessage}
        />
      </Modal>
      <Modal
        title="Confirmação"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        maskClosable={false}
        getContainer={false}
      >
        <p>Tem certeza que deseja continuar?</p>
      </Modal>
      <UpdateStatusBotModal
        visible={isBotModalVisible}
        onCancel={handleCancelBotModal}
        onUpdate={handleBotStatusUpdate}
        botStatus={selectedCustomer?.botStatus}
      />
      <CustomerImportModal
        visible={isImportModalVisible}
        onCancel={handleImportCancel}
        refetchCustomers={() => refetch()}
        resetToFirstPage={() => setCurrentPage(1)}
      />
      <CustomerDateFilterModal
        open={isDateFilterModalVisible}
        onCancel={handleDateFilterCancel}
        onApply={handleDateFilterApply}
        initialFilters={dateFilters}
      />
      <AdvancedFilterModal
        open={isAdvancedFilterModalVisible}
        onCancel={handleAdvancedFilterCancel}
        onApply={handleAdvancedFilterApply}
        initialFilters={advancedFilters}
      />
      <MaintenanceModal
        visible={isMaintenanceModalVisible}
        onClose={handleMaintenanceModalClose}
        selectedCustomers={selectedCustomers}
        onSuccess={() => {
          // Recarregar listagem mantendo seleção e filtros
          refetch();
          // Manter os clientes selecionados após o sucesso
          // A seleção será mantida automaticamente pois os IDs permanecem os mesmos
        }}
      />
      <UnmarkAiBotModal
        visible={isUnmarkModalVisible}
        onCancel={() => setIsUnmarkModalVisible(false)}
        onConfirm={handleUnmarkAsBot}
        customer={selectedCustomerForUnmark}
        loading={unmarkLoading}
      />
    </div>
  );
};

export default Customers;
